import React, { useState, useRef } from 'react';
import { Upload, FileText, Zap, TrendingDown, Calculator, CheckCircle2, Loader2, X, Camera } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const bsTranslations = {
  de: {
    errInvalidType: 'Bitte laden Sie ein Bild (JPG, PNG) oder PDF hoch.',
    errTooLarge: 'Die Datei ist zu groß. Maximale Größe: 10MB',
    errAnalysisFailed: 'Die Analyse ist fehlgeschlagen. Bitte versuchen Sie es mit einem anderen Bild.',
    aiPowered: 'KI-Powered',
    titlePre: 'Stromrechnung ',
    titleHighlight: 'Analysieren',
    subtitle: 'Laden Sie Ihre Stromrechnung hoch und unsere KI berechnet automatisch Ihr Sparpotenzial mit einer Solaranlage.',
    uploadTitle: 'Stromrechnung hochladen',
    uploadDesc: 'Ziehen Sie Ihre Rechnung hierher oder klicken Sie zum Auswählen',
    chooseFile: 'Datei auswählen',
    takePhoto: 'Foto aufnehmen',
    supportedFormats: 'Unterstützte Formate: JPG, PNG, PDF (max. 10MB)',
    previewAlt: 'Rechnung Vorschau',
    remove: 'Entfernen',
    analyzing: 'KI analysiert Ihre Rechnung...',
    analyzeNow: 'Jetzt analysieren',
    analysisDone: 'Analyse abgeschlossen!',
    savingsPotential: 'Hier ist Ihr persönliches Sparpotenzial',
    annualConsumption: 'Ihr jährlicher Verbrauch',
    annualCosts: 'Ihre jährlichen Kosten',
    solarSavings: 'Ihr Sparpotenzial mit Solar',
    annualSavings: 'Jährliche Ersparnis',
    savings20: 'Ersparnis in 20 Jahren',
    co2Savings: 'CO₂ Einsparung/Jahr',
    ourRecommendation: 'Unsere Empfehlung',
    requestConsult: 'Kostenlose Beratung anfordern',
    newBill: 'Neue Rechnung analysieren'
  },
  en: {
    errInvalidType: 'Please upload an image (JPG, PNG) or PDF.',
    errTooLarge: 'The file is too large. Maximum size: 10MB',
    errAnalysisFailed: 'The analysis failed. Please try with a different image.',
    aiPowered: 'AI-Powered',
    titlePre: 'Electricity Bill ',
    titleHighlight: 'Analysis',
    subtitle: 'Upload your electricity bill and our AI automatically calculates your savings potential with a solar system.',
    uploadTitle: 'Upload Electricity Bill',
    uploadDesc: 'Drag your bill here or click to select',
    chooseFile: 'Choose File',
    takePhoto: 'Take Photo',
    supportedFormats: 'Supported formats: JPG, PNG, PDF (max. 10MB)',
    previewAlt: 'Bill Preview',
    remove: 'Remove',
    analyzing: 'AI is analyzing your bill...',
    analyzeNow: 'Analyze Now',
    analysisDone: 'Analysis Complete!',
    savingsPotential: 'Here is your personal savings potential',
    annualConsumption: 'Your Annual Consumption',
    annualCosts: 'Your Annual Costs',
    solarSavings: 'Your Savings Potential with Solar',
    annualSavings: 'Annual Savings',
    savings20: 'Savings over 20 Years',
    co2Savings: 'CO₂ Savings/Year',
    ourRecommendation: 'Our Recommendation',
    requestConsult: 'Request Free Consultation',
    newBill: 'Analyze New Bill'
  }
};

const BillScanner = () => {
  const { language } = useLanguage();
  const t = bsTranslations[language] || bsTranslations.de;
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  const processFile = (selectedFile) => {
    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(selectedFile.type)) {
      setError(t.errInvalidType);
      return;
    }

    // Check file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError(t.errTooLarge);
      return;
    }

    setFile(selectedFile);
    setError(null);
    setResult(null);

    // Create preview for images
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      processFile(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const analyzeBill = async () => {
    if (!file) return;

    setAnalyzing(true);
    setError(null);

    try {
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const response = await fetch(`${API_URL}/api/analyze-bill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_base64: base64,
          mime_type: file.type,
          filename: file.name
        })
      });

      if (!response.ok) {
        throw new Error('Analyse fehlgeschlagen');
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('Analysis error:', err);
      setError(t.errAnalysisFailed);
    } finally {
      setAnalyzing(false);
    }
  };

  const resetScanner = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  return (
    <section id="bill-scanner" className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Zap className="w-4 h-4" />
            {t.aiPowered}
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {t.titlePre}<span className="text-amber-600">{t.titleHighlight}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {!result ? (
            <Card className="border-2 border-dashed border-amber-300 bg-white/80 backdrop-blur">
              <CardContent className="p-8">
                {!file ? (
                  // Upload Area
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className="text-center py-12 cursor-pointer hover:bg-amber-50/50 rounded-xl transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Upload className="w-10 h-10 text-amber-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {t.uploadTitle}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {t.uploadDesc}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        type="button"
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        <FileText className="w-5 h-5 mr-2" />
                        {t.chooseFile}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="border-amber-600 text-amber-600 hover:bg-amber-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          cameraInputRef.current?.click();
                        }}
                      >
                        <Camera className="w-5 h-5 mr-2" />
                        {t.takePhoto}
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500 mt-4">
                      {t.supportedFormats}
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <input
                      ref={cameraInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                ) : (
                  // File Preview & Analyze
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      {preview ? (
                        <img
                          src={preview}
                          alt={t.previewAlt}
                          className="w-32 h-40 object-cover rounded-lg border shadow-sm"
                        />
                      ) : (
                        <div className="w-32 h-40 bg-gray-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900">{file.name}</h4>
                        <p className="text-sm text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 mt-2 p-0"
                          onClick={resetScanner}
                        >
                          <X className="w-4 h-4 mr-1" />
                          {t.remove}
                        </Button>
                      </div>
                    </div>

                    {error && (
                      <div className="bg-red-50 text-red-700 p-4 rounded-lg">
                        {error}
                      </div>
                    )}

                    <Button
                      onClick={analyzeBill}
                      disabled={analyzing}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-6 text-lg"
                    >
                      {analyzing ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {t.analyzing}
                        </>
                      ) : (
                        <>
                          <Zap className="w-5 h-5 mr-2" />
                          {t.analyzeNow}
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            // Results
            <Card className="border-0 shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{t.analysisDone}</h3>
                    <p className="text-green-100">{t.savingsPotential}</p>
                  </div>
                </div>
              </div>

              <CardContent className="p-8">
                {/* Extracted Data */}
                <div className="grid sm:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 rounded-xl p-5">
                    <p className="text-sm text-gray-500 mb-1">{t.annualConsumption}</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {result.annual_consumption?.toLocaleString('de-DE')} kWh
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-5">
                    <p className="text-sm text-gray-500 mb-1">{t.annualCosts}</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {result.annual_cost?.toLocaleString('de-DE')} €
                    </p>
                  </div>
                </div>

                {/* Savings */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingDown className="w-6 h-6 text-green-600" />
                    <h4 className="text-lg font-bold text-gray-900">{t.solarSavings}</h4>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500">{t.annualSavings}</p>
                      <p className="text-2xl font-bold text-green-600">
                        {result.annual_savings?.toLocaleString('de-DE')} €
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500">{t.savings20}</p>
                      <p className="text-2xl font-bold text-green-600">
                        {result.savings_20_years?.toLocaleString('de-DE')} €
                      </p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500">{t.co2Savings}</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {result.co2_savings?.toLocaleString('de-DE')} kg
                      </p>
                    </div>
                  </div>
                </div>

                {/* Recommendation */}
                <div className="bg-sky-50 rounded-xl p-6 mb-8">
                  <div className="flex items-center gap-2 mb-3">
                    <Calculator className="w-6 h-6 text-sky-600" />
                    <h4 className="text-lg font-bold text-gray-900">{t.ourRecommendation}</h4>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{result.recommendation}</p>
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={() => window.location.href = '/beratung'}
                    className="flex-1 bg-sky-600 hover:bg-sky-700 text-white py-6 text-lg"
                  >
                    {t.requestConsult}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={resetScanner}
                    className="flex-1 border-gray-300 py-6 text-lg"
                  >
                    {t.newBill}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
};

export default BillScanner;
